import { ClipboardPen, FlaskConical, HeartPulse, NotebookPen, Pill, Users } from 'lucide-react';
import type { AdmissionCareTeamDto } from '@his/shared';
import { tr } from '@/lib/i18n-core';
import { dateOnly, dateTime, timeOnly } from '@/lib/utils';
import { Badge } from '@/components/ui/primitives';
import { ShellSummaryCard } from '@/components/clinical-shell/ShellPanel';
import { careRoleLabel } from '../../admission/AdmissionPatientBar';
import { vitalsLine } from '../../admission/vitals-line';
import type { Admission } from '../../admission/types';
import type { IpdEmrSectionId } from '../ipd-emr-sections';

export function IpdEmrOverview({
  admission,
  team,
  available,
  onOpen,
}: {
  admission: Admission;
  team: AdmissionCareTeamDto | undefined;
  available: readonly IpdEmrSectionId[];
  onOpen: (id: IpdEmrSectionId) => void;
}) {
  const opener = (id: IpdEmrSectionId) => (available.includes(id) ? () => onOpen(id) : undefined);
  const latest = admission.vitals[0];
  const readings = vitalsLine(latest);
  const work = admission.openWork ?? { labItems: 0, radiology: 0, dueDoses: 0 };
  const lastProgress = admission.progressNotes[0];
  const lastNursing = admission.nursingNotes[0];

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <ShellSummaryCard icon={HeartPulse} title={tr('ipd', 'ws_latest_vitals')} onOpen={opener('vitals')} className="lg:col-span-2">
        {readings.length > 0 ? (
          <>
            <dl className="grid grid-cols-3 gap-3 sm:grid-cols-5">
              {readings.map((reading) => (
                <div key={reading.key}>
                  <dt className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{reading.label}</dt>
                  <dd className="tabular text-base font-semibold">{reading.value}</dd>
                </div>
              ))}
            </dl>
            <p className="mt-2 text-xs text-muted-foreground">{tr('ipd', 'ws_taken_at_x', { time: `${dateOnly(latest?.recordedAt)} ${timeOnly(latest?.recordedAt)}` })}</p>
          </>
        ) : (
          <p className="text-muted-foreground">{tr('ipd', 'no_vitals_recorded')}</p>
        )}
      </ShellSummaryCard>

      <ShellSummaryCard icon={Pill} title={tr('ipd', 'ws_emr_emar')} onOpen={opener('emar')} aside={work.dueDoses > 0 ? <Badge variant="warning">{work.dueDoses}</Badge> : undefined}>
        <p>{work.dueDoses > 0 ? tr('ipd', 'ws_doses_due_x', { count: work.dueDoses }) : tr('ipd', 'ws_no_doses_due')}</p>
      </ShellSummaryCard>

      <ShellSummaryCard icon={FlaskConical} title={tr('ipd', 'investigations')} onOpen={opener('investigations')}>
        <p>{tr('ipd', 'ws_investigations_open_x', { lab: work.labItems, imaging: work.radiology })}</p>
      </ShellSummaryCard>

      <ShellSummaryCard icon={NotebookPen} title={tr('ipd', 'doctor_progress_notes')} onOpen={opener('progress-notes')}>
        {lastProgress ? (
          <>
            <p className="text-xs text-muted-foreground">{dateTime(lastProgress.createdAt)}</p>
            <p className="line-clamp-3 whitespace-pre-line">{[lastProgress.assessment, lastProgress.plan].filter(Boolean).join(' — ') || lastProgress.subjective || lastProgress.objective}</p>
          </>
        ) : (
          <p className="text-muted-foreground">{tr('ipd', 'no_progress_notes_yet')}</p>
        )}
      </ShellSummaryCard>

      <ShellSummaryCard icon={ClipboardPen} title={tr('ipd', 'nursing_notes_and_handovers')} onOpen={opener('nursing-notes')}>
        {lastNursing ? (
          <>
            <p className="text-xs text-muted-foreground">{dateTime(lastNursing.createdAt)}</p>
            <p className="line-clamp-3 whitespace-pre-line">{lastNursing.body ?? lastNursing.situation ?? lastNursing.recommendation}</p>
          </>
        ) : (
          <p className="text-muted-foreground">{tr('ipd', 'no_nursing_notes_yet')}</p>
        )}
      </ShellSummaryCard>

      <ShellSummaryCard icon={Users} title={tr('ipd', 'ws_care_team')} className="lg:col-span-2">
        <ul className="flex flex-wrap gap-2">
          {(team?.members ?? [{ id: admission.doctor.id, name: admission.doctor.name, role: 'primary_consultant' as const }]).map((member) => (
            <li key={member.id} className="inline-flex items-center gap-2 rounded-full border px-3 py-1">
              <span className="font-medium">{member.name ?? tr('ipd', 'ws_unknown_doctor')}</span>
              <Badge variant={member.role === 'primary_consultant' ? 'default' : 'secondary'}>{careRoleLabel(member.role)}</Badge>
            </li>
          ))}
        </ul>
      </ShellSummaryCard>
    </div>
  );
}
